import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Check, Copy, Edit3, Play } from 'lucide-react';
import { Badge, Button } from './UIComponents';
import type { TranscriptionSegment } from '../api/transcriptions';
import type { TranslationDisplayMode } from './TranslationSettings';
import HighlightedText from './HighlightedText';
import type { SearchMatch } from '../utils/searchUtils';

interface EditableSegmentProps {
  segment: TranscriptionSegment;
  speakerNameOverride?: string;
  isSpeakerEditing?: boolean;
  speakerMatchCount?: number;
  isEditing: boolean;
  isActive: boolean;
  editingText: string;
  onTextChange: (value: string) => void;
  onEdit: () => void;
  onSave: (text: string) => void;
  onCancel: () => void;
  onClick: () => void;
  onPlay: () => void;
  onEditSpeaker?: (segmentId: number) => void;
  onSaveSpeaker?: (segmentId: number, newSpeaker: string, scope: 'segment' | 'all_same') => void | Promise<boolean>;
  onCancelSpeaker?: () => void;
  isSavingSpeaker?: boolean;
  formatTime: (seconds: number) => string;
  isSaving?: boolean;
  displayMode?: TranslationDisplayMode;
  readOnly?: boolean;
  showPlayButton?: boolean;
  showCopyButton?: boolean;
  showEditButton?: boolean;
  // Search-related props
  searchQuery?: string;
  segmentMatches?: SearchMatch[];
  currentMatch?: SearchMatch | null;
}

const EditableSegment = React.memo(forwardRef<HTMLDivElement, EditableSegmentProps>(({
  segment,
  speakerNameOverride,
  isSpeakerEditing = false,
  speakerMatchCount = 0,
  isEditing,
  isActive,
  editingText,
  onTextChange,
  onEdit,
  onSave,
  onCancel,
  onClick,
  onPlay,
  onEditSpeaker,
  onSaveSpeaker,
  onCancelSpeaker,
  isSavingSpeaker = false,
  formatTime,
  isSaving = false,
  displayMode = 'original',
  readOnly = false,
  showPlayButton = true,
  showCopyButton = true,
  showEditButton = true,
  searchQuery = '',
  segmentMatches = [],
  currentMatch = null
}, ref) => {
  const [isCopied, setIsCopied] = useState(false);
  const [localSpeakerValue, setLocalSpeakerValue] = useState('');
  const [localSpeakerScope, setLocalSpeakerScope] = useState<'segment' | 'all_same'>('segment');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasTranslation = Boolean(segment.translation && segment.translation.trim());
  const showTranslationOnly = displayMode === 'translation';
  const showBilingual = displayMode === 'bilingual';
  const showOriginal = displayMode === 'original' || showBilingual || !hasTranslation;
  const showTranslation = (showTranslationOnly || showBilingual) && hasTranslation;
  const effectiveIsEditing = readOnly ? false : isEditing;
  const canEdit = !readOnly && showEditButton;
  const speakerName = speakerNameOverride?.trim() || segment.speaker?.trim() || 'Speaker';
  const speakerInitial = speakerName.trim().charAt(0).toUpperCase() || 'S';
  const effectiveIsSpeakerEditing = readOnly ? false : isSpeakerEditing;

  useEffect(() => {
    if (!effectiveIsSpeakerEditing) return;
    setLocalSpeakerValue(speakerName);
    setLocalSpeakerScope('segment');
  }, [effectiveIsSpeakerEditing, speakerName]);

  useEffect(() => {
    if (!effectiveIsEditing || !textareaRef.current) return;
    const textarea = textareaRef.current;
    textarea.focus();
    textarea.selectionStart = textarea.value.length;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [effectiveIsEditing, editingText]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }

    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSave(editingText);
    }
  };

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      let textToCopy = segment.text;
      if (displayMode === 'translation' && hasTranslation) {
        textToCopy = segment.translation || '';
      } else if (displayMode === 'bilingual' && hasTranslation) {
        textToCopy = `${segment.text}\n${segment.translation}`;
      }

      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const hasCurrentMatch = currentMatch !== null;

  return (
    <div
      ref={ref}
      className={`group relative px-4 py-3 transition-colors ${
        effectiveIsEditing || isActive
          ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-2 border-indigo-500'
          : hasCurrentMatch
          ? 'bg-orange-50 dark:bg-orange-900/20 border-l-2 border-orange-400 dark:border-orange-500'
          : 'hover:bg-slate-50 dark:hover:bg-zinc-800/50 border-l-2 border-transparent'
      } ${effectiveIsEditing ? '' : 'cursor-pointer'}`}
      onClick={effectiveIsEditing ? undefined : onClick}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="h-7 w-7 rounded-full bg-indigo-500 flex items-center justify-center">
            <span className="text-xs font-medium text-white">{speakerInitial}</span>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {effectiveIsSpeakerEditing ? (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <input
                    value={localSpeakerValue}
                    onChange={(e) => setLocalSpeakerValue(e.target.value)}
                    disabled={isSavingSpeaker}
                    className="w-full max-w-[220px] rounded-md border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 py-1 text-sm text-slate-800 dark:text-zinc-200 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900 disabled:bg-slate-50 dark:disabled:bg-zinc-800 disabled:text-slate-500 dark:disabled:text-zinc-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancelSpeaker?.();
                    }}
                    disabled={isSavingSpeaker}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={isSavingSpeaker}
                    onClick={(e) => {
                      e.stopPropagation();
                      void onSaveSpeaker?.(segment.id, localSpeakerValue, localSpeakerScope);
                    }}
                  >
                    Save
                  </Button>
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-slate-500 dark:text-zinc-400">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name={`speaker-scope-${segment.id}`}
                      value="segment"
                      checked={localSpeakerScope === 'segment'}
                      onChange={() => setLocalSpeakerScope('segment')}
                      disabled={isSavingSpeaker}
                      onClick={(e) => e.stopPropagation()}
                    />
                    单行
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name={`speaker-scope-${segment.id}`}
                      value="all_same"
                      checked={localSpeakerScope === 'all_same'}
                      onChange={() => setLocalSpeakerScope('all_same')}
                      disabled={isSavingSpeaker}
                      onClick={(e) => e.stopPropagation()}
                    />
                    同名（{speakerMatchCount}）
                  </label>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-slate-700 dark:text-zinc-300">{speakerName}</span>
                {onEditSpeaker && !readOnly && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditSpeaker(segment.id);
                    }}
                    className="p-1 rounded hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
                    title="Edit speaker"
                  >
                    <Edit3 className="h-3.5 w-3.5 text-slate-500 dark:text-zinc-400" />
                  </button>
                )}
              </div>
            )}
            <span className="text-xs text-slate-400 dark:text-zinc-500">
              {formatTime(segment.start)} - {formatTime(segment.end)}
            </span>
            {segment.is_modified && (
              <Badge variant="info" className="text-[10px] px-2 py-0.5">
                Edited
              </Badge>
            )}
            {effectiveIsEditing && (
              <span className="ml-auto text-xs font-medium text-indigo-600 dark:text-indigo-400">Editing</span>
            )}
          </div>

          {effectiveIsEditing ? (
            <div className="space-y-3">
              <label className="sr-only" htmlFor={`segment-edit-${segment.id}`}>
                Edit segment text
              </label>
              <textarea
                id={`segment-edit-${segment.id}`}
                ref={textareaRef}
                value={editingText}
                onChange={(event) => onTextChange(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSaving}
                rows={1}
                className="w-full resize-none rounded-md border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-slate-800 dark:text-zinc-200 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900 disabled:bg-slate-50 dark:disabled:bg-zinc-800 disabled:text-slate-500 dark:disabled:text-zinc-500"
                onClick={(event) => event.stopPropagation()}
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    onCancel();
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  isLoading={isSaving}
                  onClick={(event) => {
                    event.stopPropagation();
                    onSave(editingText);
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {showOriginal && (
                <p className="text-sm text-slate-800 dark:text-zinc-200 leading-relaxed">
                  <HighlightedText
                    text={segment.text}
                    searchQuery={searchQuery}
                    textType="original"
                    segmentMatches={segmentMatches}
                    currentMatch={currentMatch}
                  />
                </p>
              )}
              {showTranslation && (
                <p className="text-sm text-slate-500 dark:text-zinc-400 leading-relaxed italic">
                  <HighlightedText
                    text={segment.translation || ''}
                    searchQuery={searchQuery}
                    textType="translation"
                    segmentMatches={segmentMatches}
                    currentMatch={currentMatch}
                  />
                </p>
              )}
            </div>
          )}
        </div>

        {!effectiveIsEditing && (showPlayButton || showCopyButton || canEdit) && (
          <div className="absolute right-4 top-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {showPlayButton && (
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onPlay();
                }}
                className="p-1.5 hover:bg-white dark:hover:bg-zinc-700 rounded transition-colors shadow-sm bg-white/80 dark:bg-zinc-800/80 border border-transparent dark:border-zinc-700"
                title="Play from here"
              >
                <Play className="h-3.5 w-3.5 text-slate-600 dark:text-zinc-400" />
              </button>
            )}
            {showCopyButton && (
              <button
                onClick={handleCopy}
                className="p-1.5 hover:bg-white dark:hover:bg-zinc-700 rounded transition-colors shadow-sm bg-white/80 dark:bg-zinc-800/80 border border-transparent dark:border-zinc-700"
                title={isCopied ? "Copied!" : "Copy text"}
              >
                {isCopied ? (
                  <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-slate-600 dark:text-zinc-400" />
                )}
              </button>
            )}
            {canEdit && (
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit();
                }}
                className="p-1.5 hover:bg-white dark:hover:bg-zinc-700 rounded transition-colors shadow-sm bg-white/80 dark:bg-zinc-800/80 border border-transparent dark:border-zinc-700"
                title="Edit segment"
              >
                <Edit3 className="h-3.5 w-3.5 text-slate-600 dark:text-zinc-400" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}));

EditableSegment.displayName = 'EditableSegment';

export default EditableSegment;
